import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Nav,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaCopy,
  FaEdit,
  FaFileAlt,
  FaPause,
  FaPlay,
  FaPlus,
  FaSync,
  FaTrash,
} from 'react-icons/fa';
import SchedulerApi from '../../../api/SchedulerApi';
import ScheduledJobCreateModal from './ScheduledJobCreateModal';
import {
  isCustomScriptJob,
  scheduledJobActionLabel,
} from './scheduledJobActions';
import { ScheduledJobNameLink } from './scheduledJobLinks';
import { resolveEndDateFromRow, resolveStartDateFromRow } from './schedulerRepeatUi';

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'history', label: 'History' },
];

function formatDt(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function formatType(row) {
  try {
    let c = row.config;
    if (typeof c === 'string') c = JSON.parse(c);
    if (c?.scheduledAction) {
      return scheduledJobActionLabel(c.scheduledAction);
    }
  } catch (_) {
    /* ignore */
  }
  if (row.scheduler_type === 'custom_script' || row.action_type === 'run_custom_script') {
    return scheduledJobActionLabel('custom_script');
  }
  return row.scheduler_type || row.action_type || '—';
}

function historyStatusBadge(status) {
  const st = String(status || '').toLowerCase();
  if (st === 'failed') return 'danger';
  if (st === 'success' || st === 'completed') return 'success';
  if (st === 'running') return 'warning';
  return 'secondary';
}

const ScheduledJobsManager = () => {
  const api = useMemo(() => new SchedulerApi(), []);
  const [tab, setTab] = useState('active');
  const [rows, setRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [selectedHistoryIds, setSelectedHistoryIds] = useState(() => new Set());
  const [logsModal, setLogsModal] = useState({ open: false, row: null, events: [] });
  const [formModal, setFormModal] = useState({ open: false, mode: 'create', row: null });
  const [formBusy, setFormBusy] = useState(false);
  const [toast, setToast] = useState('');

  const isHistoryTab = tab === 'history';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelectedHistoryIds(new Set());
    try {
      if (isHistoryTab) {
        const res = await api.listExecutionHistory();
        const payload = res?.data;
        if (payload?.success) {
          setHistoryRows(Array.isArray(payload.data) ? payload.data : []);
          setRows([]);
        } else {
          setError(payload?.errors || 'Failed to load execution history');
          setHistoryRows([]);
        }
      } else {
        const res = await api.list(tab);
        const payload = res?.data;
        if (payload?.success) {
          setRows(Array.isArray(payload.data) ? payload.data : []);
          setHistoryRows([]);
        } else {
          setError(payload?.errors || 'Failed to load scheduled jobs');
          setRows([]);
        }
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load');
      setRows([]);
      setHistoryRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, tab, isHistoryTab]);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 4000);
  };

  const handleRunNow = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.runNow(row.id);
      if (res?.data?.success) {
        showToast('Job completed.');
        const live = res?.data?.liveNotification;
        if (live) {
          window.dispatchEvent(
            new CustomEvent('ibs-system-notification', {
              detail: { notification: live },
            })
          );
        }
        window.dispatchEvent(
          new CustomEvent('ibs-refresh-system-notifications', {
            detail: { showToast: !live },
          })
        );
        load();
      } else {
        showToast(res?.data?.errors || 'Run failed');
      }
    } catch (e) {
      showToast(e?.response?.data?.errors || e.message || 'Run failed');
    } finally {
      setBusyId(null);
    }
  };

  const handlePause = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.pause(row.id);
      if (res?.data?.success) {
        showToast('Job paused.');
        load();
      } else {
        showToast(res?.data?.errors || 'Pause failed');
      }
    } catch (e) {
      showToast(e?.response?.data?.errors || e.message || 'Pause failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleActivate = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.update(row.id, { is_active: true });
      if (res?.data?.success) {
        showToast('Job activated.');
        setTab('active');
        load();
      } else {
        showToast(res?.data?.errors || 'Activate failed');
      }
    } catch (e) {
      showToast(e?.response?.data?.errors || e.message || 'Activate failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleClone = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.clone(row.id);
      if (res?.data?.success) {
        showToast('Job cloned.');
        setTab('active');
        load();
      } else {
        showToast(res?.data?.errors || 'Clone failed');
      }
    } catch (e) {
      showToast(e?.response?.data?.errors || e.message || 'Clone failed');
    } finally {
      setBusyId(null);
    }
  };

  const openLogs = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.getLogs(row.id);
      const payload = res?.data;
      if (payload?.success) {
        setLogsModal({
          open: true,
          row: payload.data?.row || row,
          events: payload.data?.events || [],
        });
      } else {
        showToast(payload?.errors || 'Could not load logs');
      }
    } catch (e) {
      showToast(e?.response?.data?.errors || e.message || 'Could not load logs');
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = () => {
    setFormModal({ open: true, mode: 'create', row: null });
  };

  const openEdit = (row) => {
    if (!isCustomScriptJob(row)) {
      showToast('Only custom script jobs can be edited.');
      return;
    }
    setFormModal({ open: true, mode: 'edit', row });
  };

  const closeFormModal = () => {
    setFormModal({ open: false, mode: 'create', row: null });
  };

  const handleCreate = async (body) => {
    setFormBusy(true);
    try {
      const res = await api.create(body);
      if (res?.data?.success) {
        showToast('Scheduled job created.');
        closeFormModal();
        setTab('active');
        load();
      } else {
        showToast(res?.data?.errors || 'Create failed');
      }
    } catch (e) {
      showToast(e?.response?.data?.errors || e.message || 'Create failed');
    } finally {
      setFormBusy(false);
    }
  };

  const handleSave = async (id, body) => {
    setFormBusy(true);
    try {
      const res = await api.update(id, { ...body, action: 'custom_script' });
      if (res?.data?.success) {
        showToast('Scheduled job saved.');
        closeFormModal();
        load();
      } else {
        showToast(res?.data?.errors || 'Save failed');
      }
    } catch (e) {
      showToast(e?.response?.data?.errors || e.message || 'Save failed');
    } finally {
      setFormBusy(false);
    }
  };

  const toggleHistorySelect = (id) => {
    setSelectedHistoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleHistorySelectAll = (checked) => {
    if (!checked) {
      setSelectedHistoryIds(new Set());
      return;
    }
    setSelectedHistoryIds(new Set(historyRows.map((r) => r.id).filter(Boolean)));
  };

  const handleDeleteHistoryRows = async (ids) => {
    const list = [...ids].filter(Boolean);
    if (list.length === 0) return;
    setHistoryBusy(true);
    try {
      if (list.length === 1) {
        const res = await api.deleteExecutionHistoryRow(list[0]);
        if (res?.data?.success) {
          showToast('History entry deleted.');
          load();
        } else {
          showToast(res?.data?.errors || 'Delete failed');
        }
      } else {
        const res = await api.deleteExecutionHistory(list);
        if (res?.data?.success) {
          showToast(`Deleted ${res.data.deleted ?? list.length} history entr${list.length === 1 ? 'y' : 'ies'}.`);
          load();
        } else {
          showToast(res?.data?.errors || 'Delete failed');
        }
      }
    } catch (e) {
      showToast(e?.response?.data?.errors || e.message || 'Delete failed');
    } finally {
      setHistoryBusy(false);
    }
  };

  const handleDeleteHistoryOne = (historyRow) => {
    if (!historyRow?.id) return;
    handleDeleteHistoryRows([historyRow.id]);
  };

  const handleBulkDeleteHistory = () => {
    handleDeleteHistoryRows([...selectedHistoryIds]);
  };

  const handleDeleteLogEvent = async (ev) => {
    if (!ev?.id) return;
    setHistoryBusy(true);
    try {
      const res = await api.deleteExecutionHistoryRow(ev.id);
      if (res?.data?.success) {
        showToast('Log entry deleted.');
        setLogsModal((m) => ({
          ...m,
          events: (m.events || []).filter((e) => e.id !== ev.id),
        }));
        if (isHistoryTab) load();
      } else {
        showToast(res?.data?.errors || 'Delete failed');
      }
    } catch (e) {
      showToast(e?.response?.data?.errors || e.message || 'Delete failed');
    } finally {
      setHistoryBusy(false);
    }
  };

  const canRunNow = (row) =>
    Boolean(row.is_active) && (row.status === 'pending' || row.status === 'failed');

  const allHistorySelected =
    historyRows.length > 0 && historyRows.every((r) => selectedHistoryIds.has(r.id));
  const someHistorySelected = selectedHistoryIds.size > 0;

  const tabHint =
    tab === 'active'
      ? 'Active custom script jobs (pending or failed).'
      : tab === 'history'
        ? 'Execution history from scheduled_job_history.'
        : 'Inactive custom script jobs — edit to reactivate.';

  return (
    <div className="p-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0 d-flex align-items-center gap-2">
            <FaCalendarAlt className="text-primary" />
            Scheduled Jobs
          </h4>
          <div className="text-muted small">{tabHint}</div>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="primary" size="sm" onClick={openCreate}>
            <FaPlus className="me-1" />
            New job
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading || historyBusy}>
            <FaSync /> Refresh
          </Button>
        </Col>
      </Row>

      {toast ? (
        <div className="alert alert-info py-2 small mb-3" role="status">
          {toast}
        </div>
      ) : null}
      {error ? (
        <div className="alert alert-danger py-2 small mb-3" role="alert">
          {error}
        </div>
      ) : null}

      <Card className="shadow-sm">
        <Card.Header className="bg-white border-bottom-0 pt-3 pb-0">
          <Nav variant="tabs" className="border-bottom-0">
            {TABS.map((t) => (
              <Nav.Item key={t.key}>
                <Nav.Link
                  active={tab === t.key}
                  onClick={() => setTab(t.key)}
                  style={{ cursor: 'pointer' }}
                >
                  {t.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Card.Header>
        <Card.Body className="p-0">
          {isHistoryTab && someHistorySelected ? (
            <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom bg-light">
              <span className="small text-muted">{selectedHistoryIds.size} selected</span>
              <Button
                variant="outline-danger"
                size="sm"
                disabled={historyBusy}
                onClick={handleBulkDeleteHistory}
              >
                {historyBusy ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <FaTrash className="me-1" />
                )}
                Delete selected
              </Button>
            </div>
          ) : null}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" /> Loading…
            </div>
          ) : isHistoryTab ? (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '2.5rem' }}>
                      <Form.Check
                        type="checkbox"
                        checked={allHistorySelected}
                        onChange={(e) => toggleHistorySelectAll(e.target.checked)}
                        disabled={historyRows.length === 0 || historyBusy}
                        aria-label="Select all history"
                      />
                    </th>
                    <th>Job</th>
                    <th>When</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Trigger</th>
                    <th>Details</th>
                    <th className="text-center align-middle" style={{ width: '4.5rem' }}>
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        No execution history yet.
                      </td>
                    </tr>
                  ) : (
                    historyRows.map((h) => (
                      <tr key={h.id}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedHistoryIds.has(h.id)}
                            onChange={() => toggleHistorySelect(h.id)}
                            disabled={historyBusy}
                            aria-label={`Select history ${h.id}`}
                          />
                        </td>
                        <td className="small text-break">
                          <ScheduledJobNameLink row={h} className="fw-semibold small text-break" />
                        </td>
                        <td className="text-nowrap small">
                          {formatDt(h.finished_at || h.started_at || h.createddate)}
                        </td>
                        <td className="small">
                          {h.execution_label || h.execution_kind || '—'}
                        </td>
                        <td>
                          <Badge bg={historyStatusBadge(h.status)}>{h.status || '—'}</Badge>
                        </td>
                        <td className="small">{h.trigger_source || '—'}</td>
                        <td className="small text-break">{h.message || '—'}</td>
                        <td className="text-center align-middle">
                          <div className="d-flex justify-content-center align-items-center py-1 px-2">
                            <Button
                              variant="link"
                              className="text-danger p-1 lh-1"
                              title="Delete"
                              disabled={historyBusy}
                              onClick={() => handleDeleteHistoryOne(h)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Schedule start</th>
                    <th>Next run</th>
                    <th>End</th>
                    <th>Last run</th>
                    <th>Status</th>
                    <th>Active</th>
                    <th>Owner</th>
                    <th>Repeat</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center text-muted py-4">
                        {tab === 'inactive'
                          ? 'No inactive custom script jobs.'
                          : 'No jobs in this view.'}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const scriptJob = isCustomScriptJob(row);
                      const inactiveTab = tab === 'inactive';
                      return (
                        <tr key={row.id}>
                          <td>
                            <ScheduledJobNameLink row={row} />
                          </td>
                          <td>
                            <Badge bg="light" text="dark" className="text-uppercase">
                              {formatType(row)}
                            </Badge>
                          </td>
                          <td className="text-nowrap small">
                            {formatDt(
                              row.start_date ||
                                (resolveStartDateFromRow(row)
                                  ? new Date(resolveStartDateFromRow(row)).toISOString()
                                  : null)
                            )}
                          </td>
                          <td className="text-nowrap small">{formatDt(row.next_run)}</td>
                          <td className="text-nowrap small">
                            {formatDt(
                              row.end_date ||
                                (resolveEndDateFromRow(row)
                                  ? new Date(resolveEndDateFromRow(row)).toISOString()
                                  : null)
                            )}
                          </td>
                          <td className="text-nowrap small">{formatDt(row.last_run)}</td>
                          <td>
                            <Badge
                              bg={
                                row.status === 'completed'
                                  ? 'success'
                                  : row.status === 'failed'
                                    ? 'danger'
                                    : row.status === 'running'
                                      ? 'warning'
                                      : row.status === 'cancelled'
                                        ? 'secondary'
                                        : 'primary'
                              }
                            >
                              {row.status || '—'}
                            </Badge>
                          </td>
                          <td>
                            {row.is_active ? (
                              <Badge bg="success">Yes</Badge>
                            ) : (
                              <Badge bg="secondary">No</Badge>
                            )}
                          </td>
                          <td className="small text-break">
                            {(row.owner_display_name && row.owner_display_name.trim()) ||
                              row.ownerid ||
                              '—'}
                          </td>
                          <td className="small">{row.repeat_type || '—'}</td>
                          <td className="text-end">
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="outline-primary"
                                size="sm"
                                disabled={busyId === row.id}
                              >
                                {busyId === row.id ? (
                                  <Spinner size="sm" animation="border" />
                                ) : (
                                  'Actions'
                                )}
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {inactiveTab ? (
                                  <>
                                    <Dropdown.Item onClick={() => openEdit(row)} disabled={!scriptJob}>
                                      <FaEdit className="me-2" />
                                      Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleActivate(row)} disabled={!scriptJob}>
                                      <FaPlay className="me-2 text-success" />
                                      Activate
                                    </Dropdown.Item>
                                  </>
                                ) : (
                                  <>
                                    <Dropdown.Item
                                      onClick={() => handleRunNow(row)}
                                      disabled={!canRunNow(row)}
                                    >
                                      <FaPlay className="me-2 text-success" />
                                      Run now
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handlePause(row)}
                                      disabled={!row.is_active}
                                    >
                                      <FaPause className="me-2 text-warning" />
                                      Pause
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleClone(row)}>
                                      <FaCopy className="me-2" />
                                      Clone
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => openLogs(row)}>
                                      <FaFileAlt className="me-2" />
                                      View logs
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => openEdit(row)}
                                      disabled={!scriptJob}
                                    >
                                      <FaEdit className="me-2" />
                                      Edit
                                    </Dropdown.Item>
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={logsModal.open}
        onHide={() => setLogsModal({ open: false, row: null, events: [] })}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Job logs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {logsModal.row ? (
            <div className="mb-3 small text-muted">
              <ScheduledJobNameLink row={logsModal.row} className="fw-bold" />
              <div>ID: {logsModal.row.id}</div>
            </div>
          ) : null}
          {(logsModal.events || []).filter((ev) => ev.id).length > 0 ? (
            <div className="table-responsive">
              <Table size="sm" hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>When</th>
                    <th>Job type</th>
                    <th>Status</th>
                    <th>Trigger</th>
                    <th>Details</th>
                    <th className="text-center align-middle" style={{ width: '4.5rem' }}>
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(logsModal.events || [])
                    .filter((ev) => ev.id)
                    .map((ev) => (
                      <tr key={ev.id}>
                        <td className="text-nowrap small">
                          {formatDt(ev.finished_at || ev.started_at || ev.at)}
                        </td>
                        <td className="small">
                          {ev.execution_label || ev.execution_kind || '—'}
                        </td>
                        <td>
                          {ev.status ? (
                            <Badge bg={historyStatusBadge(ev.status)}>{ev.status}</Badge>
                          ) : (
                            <Badge
                              bg={
                                ev.type === 'error'
                                  ? 'danger'
                                  : ev.type === 'success'
                                    ? 'success'
                                    : 'light'
                              }
                              text={ev.type === 'error' || ev.type === 'success' ? undefined : 'dark'}
                            >
                              {ev.type}
                            </Badge>
                          )}
                        </td>
                        <td className="small">{ev.trigger || '—'}</td>
                        <td className="small text-break">{ev.message}</td>
                        <td className="text-center align-middle">
                          <div className="d-flex justify-content-center align-items-center py-1 px-2">
                            <Button
                              variant="link"
                              className="text-danger p-1 lh-1"
                              title="Delete"
                              disabled={historyBusy}
                              onClick={() => handleDeleteLogEvent(ev)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          ) : (logsModal.events || []).length > 0 ? (
            <div className="table-responsive">
              <Table size="sm" hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>When</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {(logsModal.events || []).map((ev, idx) => (
                    <tr key={idx}>
                      <td className="text-nowrap small">{formatDt(ev.at)}</td>
                      <td className="small text-break">{ev.message}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-muted small">No execution history yet.</div>
          )}
        </Modal.Body>
      </Modal>

      <ScheduledJobCreateModal
        show={formModal.open}
        onHide={closeFormModal}
        mode={formModal.mode}
        editRow={formModal.row}
        onCreate={handleCreate}
        onSave={handleSave}
        busy={formBusy}
      />
    </div>
  );
};

export default ScheduledJobsManager;
